"use client";

import React, { useMemo, useState } from "react";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Checkbox } from "~/components/ui/checkbox";
import { Switch } from "~/components/ui/switch";
import {
  buildDownloadRows,
  buildDefaultDownloadSelection,
  getRetryChildKeys,
  DownloadRowModel,
  DownloadRowKey,
} from "~/lib/downloadOptions";
import { GiCheckMark } from "react-icons/gi";
import { FiRefreshCw } from "react-icons/fi";
import { IoHourglassOutline } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import { downloadResponseAsFile } from "~/utils/browserDownload";

export type DownloadSolutionsDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  problemSetId?: number | string;
  rows?: DownloadRowModel[];
  problems?: Array<{ problemId: number }>;
  attempts?: Array<{
    problemId: number;
    finalResult?: string;
    retryCountUsed?: number;
    finalExternalHint?: boolean;
  }>;
};

export default function DownloadSolutionsDialog({
  isOpen,
  onClose,
  problemSetId,
  rows,
  problems,
  attempts,
}: DownloadSolutionsDialogProps) {
  const computedRows: DownloadRowModel[] = useMemo(() => {
    if (rows) return rows;
    return buildDownloadRows(problems ?? [], attempts ?? []);
  }, [rows, problems, attempts]);

  const filterVisibleRows = (
    inputRows: DownloadRowModel[],
  ): DownloadRowModel[] => {
    return inputRows.reduce<DownloadRowModel[]>((acc, row) => {
      const filteredChildren = row.children
        ? filterVisibleRows(row.children)
        : [];
      const hasOwnTasks = row.tasks.length > 0;
      const hasVisibleChildren = filteredChildren.length > 0;

      if (hasOwnTasks || hasVisibleChildren) {
        acc.push({
          ...row,
          children: filteredChildren,
        });
      }

      return acc;
    }, []);
  };

  const visibleRows = useMemo(
    () => filterVisibleRows(computedRows),
    [computedRows],
  );

  const [checked, setChecked] = useState<Record<DownloadRowKey, boolean>>(
    buildDefaultDownloadSelection(visibleRows),
  );

  React.useEffect(() => {
    setChecked(buildDefaultDownloadSelection(visibleRows));
  }, [visibleRows]);

  const [showDetails, setShowDetails] = useState(false);

  // new download option switches (step 2)
  const [includeStatusInTaskName, setIncludeStatusInTaskName] = useState(true);
  const [includeIncorrectSolutions, setIncludeIncorrectSolutions] =
    useState(true);
  const [forRetry, setForRetry] = useState(false);

  // compute retry child keys once so we can drive cascade logic
  const retryChildKeys = useMemo(
    () => getRetryChildKeys(visibleRows),
    [visibleRows],
  );

  // derive selected problem IDs based on current checkbox state and visible rows
  const selectedProblemIds = useMemo(() => {
    const ids: number[] = [];
    const seen = new Set<number>();

    const traverse = (rows: DownloadRowModel[]) => {
      for (const row of rows) {
        if (checked[row.key]) {
          for (const task of row.tasks) {
            if (!seen.has(task.problemId)) {
              seen.add(task.problemId);
              ids.push(task.problemId);
            }
          }
        }
        if (row.children) {
          traverse(row.children);
        }
      }
    };

    traverse(visibleRows);
    return ids;
  }, [visibleRows, checked]);

  const handleCheckedChange = (rowKey: DownloadRowKey, value: boolean) => {
    setChecked((prev) => {
      const next: Record<DownloadRowKey, boolean> = {
        ...prev,
        [rowKey]: value,
      };

      // parent toggle cascades to children
      if (rowKey === "retry-group") {
        retryChildKeys.forEach((childKey) => {
          next[childKey] = value;
        });
      }

      // child toggle affects parent state
      if (retryChildKeys.includes(rowKey)) {
        const allChildrenChecked = retryChildKeys.every((k) =>
          k === rowKey ? value : prev[k],
        );
        next["retry-group"] = allChildrenChecked;
      }

      return next;
    });
  };

  const renderCategoryLabel = (row: DownloadRowModel) => {
    const retryMatch = row.key.match(
      /^retry-(\d+)-(win|draw|external-hint|loss)$/,
    );

    const renderTypeIcon = (
      type: "win" | "draw" | "external-hint" | "loss",
    ) => {
      if (type === "win") {
        return <GiCheckMark className="text-green-500" />;
      }
      if (type === "draw") {
        return <span className="text-yellow-300">½</span>;
      }
      if (type === "external-hint") {
        return <span className="text-blue-500">½</span>;
      }
      return <RxCross2 className="text-red-500" />;
    };

    if (row.key === "pending") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <IoHourglassOutline />
          <span>:</span>
        </span>
      );
    }

    if (row.key === "draw") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-yellow-300">½</span>
          <span>:</span>
        </span>
      );
    }

    if (row.key === "loss") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <RxCross2 className="text-red-500" />
          <span>:</span>
        </span>
      );
    }

    if (row.key === "external-hint") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="text-blue-500">½</span>
          <span>:</span>
        </span>
      );
    }

    if (row.key === "first-try-solved") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <GiCheckMark className="text-green-500" />
          <span>:</span>
        </span>
      );
    }

    if (row.key === "retry-group") {
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          <span className="flex items-center text-pink-400">
            <FiRefreshCw />
          </span>
          <span>:</span>
        </span>
      );
    }

    if (retryMatch) {
      const retryCount = retryMatch[1];
      const retryType = retryMatch[2] as
        | "win"
        | "draw"
        | "external-hint"
        | "loss";
      return (
        <span className="flex items-center gap-1 whitespace-nowrap">
          {renderTypeIcon(retryType)}
          <span className="flex items-center text-pink-400">
            <FiRefreshCw />
            <span className="ml-1">{retryCount}</span>
          </span>
          <span>:</span>
        </span>
      );
    }

    return <span>{row.label}</span>;
  };

  const renderRow = (row: DownloadRowModel, indent = 0) => {
    return (
      <div key={row.key} className="mb-2" style={{ marginLeft: indent * 16 }}>
        <div className="flex flex-wrap items-center gap-2">
          <Checkbox
            checked={!!checked[row.key]}
            onCheckedChange={(v) => {
              handleCheckedChange(row.key, !!v);
            }}
            data-testid={`download-row-${row.key}`}
            role="checkbox"
          />
          {renderCategoryLabel(row)}
          {row.tasks.length > 0 && (
            <span data-testid={`download-count-${row.key}`}>
              {row.tasks.length}
            </span>
          )}
          {showDetails && row.tasks.length > 0 && (
            <span
              data-testid={`download-details-${row.key}`}
              className="text-sm text-muted-foreground"
            >
              <span>(</span>
              {row.tasks.map((task, index) => (
                <React.Fragment key={`task-${row.key}-${task.problemId}`}>
                  {problemSetId ? (
                    <Link
                      href={`/problem-sets/${problemSetId}/problems/${task.problemId}`}
                      onClick={onClose}
                      data-testid={`download-task-${row.key}-${task.problemId}`}
                      className="underline"
                    >
                      {task.name}
                    </Link>
                  ) : (
                    <span
                      data-testid={`download-task-${row.key}-${task.problemId}`}
                    >
                      {task.name}
                    </span>
                  )}
                  {index < row.tasks.length - 1 ? ", " : null}
                </React.Fragment>
              ))}
              <span>)</span>
            </span>
          )}
        </div>

        {row.children?.map((child) => renderRow(child, indent + 1))}
      </div>
    );
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(v: boolean) => {
        if (!v) onClose();
      }}
    >
      <DialogContent showCloseButton={false} className="max-w-4xl">
        <div data-testid="download-solutions-dialog">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Параметры скачивания</DialogTitle>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2">
                  <span className="text-sm">Подробно</span>
                  <Switch
                    checked={showDetails}
                    onCheckedChange={setShowDetails}
                    data-testid="download-details-switch"
                    role="switch"
                  />
                </label>
              </div>
            </div>
          </DialogHeader>
          <div className="mt-3">
            <div className="max-h-[80vh] w-full overflow-y-auto">
              {showDetails && (
                <div
                  data-testid="download-details-content"
                  className="hidden"
                />
              )}
              {visibleRows.map((row) => renderRow(row))}
            </div>
          </div>
          <div className="mt-4 flex items-end justify-between gap-4">
            <button
              data-testid="download-submit-button"
              className="btn"
              type="button"
              disabled={selectedProblemIds.length === 0}
              onClick={async () => {
                // prepare payload exactly as specified
                try {
                  const res = await fetch("/api/download", {
                    method: "POST",
                    headers: {
                      "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                      problemIds: selectedProblemIds,
                      includeStatusInTaskName,
                      includeIncorrectSolutions,
                      forRetry,
                    }),
                  });

                  if (res.ok) {
                    await downloadResponseAsFile(res, "download.pgn");
                    onClose();
                  }
                  // if not ok, silently keep dialog open
                } catch (e) {
                  // network or other error - keep dialog open
                  // do not propagate
                }
              }}
            >
              Скачать
            </button>
            <div className="ml-auto flex flex-col items-end gap-2">
              <label className="flex items-center gap-2">
                <span>Статус задачи в имени задачи</span>
                <Switch
                  checked={includeStatusInTaskName}
                  onCheckedChange={setIncludeStatusInTaskName}
                  data-testid="download-option-status-in-name"
                  role="switch"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Выгружать неверные решения</span>
                <Switch
                  checked={includeIncorrectSolutions}
                  onCheckedChange={setIncludeIncorrectSolutions}
                  data-testid="download-option-include-incorrect"
                  role="switch"
                />
              </label>
              <label className="flex items-center gap-2">
                <span>Для повторного решения</span>
                <Switch
                  checked={forRetry}
                  onCheckedChange={setForRetry}
                  data-testid="download-option-for-retry"
                  role="switch"
                />
              </label>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
